import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { LayoutGroup, MotionConfig, motion } from 'framer-motion'
import {
  IconChevronDown,
  IconFlame,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconSelector,
  IconSparkles,
  IconUserCog,
  IconX,
} from '@tabler/icons-react'
import { useAuth } from '../Context/AuthContext'
import { useAuthAdmin } from '../Context/useAuthAdmin'
import { useProfileImageUrl } from '../Context/useProfileImageUrl'
import { useAccessScope } from '../hooks/queries/useOrgGroups'
import { useTodaySummary } from '../hooks/queries/useGamificationStreaks'
import { fetchWithAuth } from '../lib/apiClient'
import { formatPlanTierLabel, normalizePlanTier } from '../lib/subscriptionPlans'
import { getPrimaryNavItems, getSecondaryNavItems, hasAppSideNav, APP_SIDENAV_TOGGLE_EVENT } from '../lib/sideNavConfig'
import { QUICK_ACTION_TINT } from './dashboard/quickActions'

// Matches the navbar's md:hidden menu button: below this the nav is a drawer opened from the navbar.
const MOBILE_BREAKPOINT = 768
// Below this, a first visit starts on the icon rail so tablets keep room for content.
const WIDE_BREAKPOINT = 1180
const EXPANDED_WIDTH = 260
const RAIL_WIDTH = 72
const COLLAPSED_KEY = 'astra:sidenav-collapsed'

const ITEM_TINT = { dashboard: QUICK_ACTION_TINT['ai-avatar'], ...QUICK_ACTION_TINT }

function readCollapsedPreference() {
  try {
    const saved = localStorage.getItem(COLLAPSED_KEY)
    if (saved === '1') return true
    if (saved === '0') return false
  } catch {
    // Storage may be unavailable; fall back to the screen-width default.
  }
  return typeof window !== 'undefined' && window.innerWidth < WIDE_BREAKPOINT
}

function isPathActive(pathname, item) {
  const own = item.exact
    ? pathname === item.to
    : pathname === item.to || pathname.startsWith(`${item.to}/`)
  // Some sections (e.g. Assessments) have children under a different prefix than the parent's own `to`.
  return own || Boolean(item.children?.some((child) => pathname === child.to || pathname.startsWith(`${child.to}/`)))
}

function themeClasses(dark) {
  return dark
    ? {
        panel: 'border-sky-400/15 text-white/90',
        muted: 'text-white/60',
        row: 'text-white/75 hover:bg-white/5 hover:text-white',
        rowActive: 'font-semibold text-white',
        pill: 'bg-white/10 ring-1 ring-inset ring-white/10',
        divider: 'border-sky-400/15',
        iconButton: 'text-white/70 hover:bg-white/10 hover:text-white',
        childActive: 'bg-white/10 font-semibold text-sky-200',
        card: 'border-white/10 bg-white/5 hover:bg-white/10',
        menu: 'border-white/10 bg-[#0b1224] text-white/90',
        menuRow: 'hover:bg-white/10',
        focus: 'focus-visible:ring-sky-300/70',
      }
    : {
        panel: 'border-d-outline-variant/12 text-d-on-surface',
        muted: 'text-d-on-surface-variant/70',
        row: 'text-d-on-surface-variant hover:bg-white/70 hover:text-d-on-surface',
        rowActive: 'font-semibold text-d-on-surface',
        pill: 'bg-white shadow-[0_3px_10px_rgba(27,16,51,0.07)] ring-1 ring-inset ring-d-outline-variant/10',
        divider: 'border-d-outline-variant/12',
        iconButton: 'text-d-on-surface-variant hover:bg-white/80 hover:text-d-on-surface',
        childActive: 'bg-d-primary/10 font-semibold text-d-primary',
        card: 'border-d-outline-variant/15 bg-white/70 hover:bg-white',
        menu: 'border-d-outline-variant/15 bg-white text-d-on-surface',
        menuRow: 'hover:bg-d-surface-low',
        focus: 'focus-visible:ring-d-primary/40',
      }
}

/** Label tooltip for the icon rail, portaled so the scrolling nav doesn't clip it. */
function RailTooltip({ tip }) {
  if (!tip) return null
  return createPortal(
    <span
      role="tooltip"
      className="pointer-events-none fixed z-[1200] -translate-y-1/2 whitespace-nowrap rounded-lg bg-[#1b1033] px-2.5 py-1.5 text-xs font-semibold text-white shadow-lg"
      style={{ top: tip.top, left: tip.left }}
    >
      {tip.label}
    </span>,
    document.body
  )
}

function ProfileCard({ user, collapsed, dark, planLabel, streak, accountItems, onAction }) {
  const t = themeClasses(dark)
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const name = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'You'
  const avatar = useProfileImageUrl(user, user?.user_metadata?.full_name, user?.email)

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const lit = streak?.current > 0

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${name}`}
        onClick={() => setOpen((value) => !value)}
        className={`flex w-full items-center gap-2.5 rounded-2xl border p-2 text-left transition focus:outline-none focus-visible:ring-2 ${t.card} ${t.focus} ${collapsed ? 'justify-center' : ''}`}
      >
        <span className="relative shrink-0">
          <img src={avatar} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-white" />
          {collapsed && lit ? (
            <span className="absolute -bottom-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[9px] font-bold text-white ring-2 ring-white">
              {streak.current}
            </span>
          ) : null}
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold">{name}</span>
              <span className={`flex items-center gap-1.5 truncate text-[11px] ${t.muted}`}>
                {planLabel ? <span className="truncate">{planLabel}</span> : null}
                {lit ? (
                  <span
                    className="inline-flex shrink-0 items-center gap-0.5 font-semibold text-orange-500"
                    title={`${streak.current}-day streak`}
                  >
                    <IconFlame size={12} stroke={2.2} />
                    {streak.current}
                  </span>
                ) : null}
              </span>
            </span>
            <IconSelector size={16} stroke={1.8} className={`shrink-0 ${t.muted}`} />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute z-[960] w-56 overflow-hidden rounded-2xl border p-1.5 shadow-[0_18px_40px_rgba(27,16,51,0.18)] ${t.menu} ${
            collapsed ? 'bottom-0 left-full ml-3' : 'bottom-full left-0 mb-2'
          }`}
        >
          {accountItems.map((item, index) => {
            const Icon = item.icon
            const danger = item.tone === 'danger'
            return (
              <div key={item.key}>
                {danger && index > 0 ? <div className={`my-1 border-t ${t.divider}`} /> : null}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false)
                    onAction(item)
                  }}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm transition ${
                    danger ? 'text-rose-600 hover:bg-rose-50' : t.menuRow
                  }`}
                >
                  <Icon size={17} stroke={1.8} />
                  {item.label}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function AppSideNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { isSuperAdmin, isAdmin, isCreator, isTrainer } = useAuthAdmin()
  const accessScopeQuery = useAccessScope(!!user?.id)
  const effectiveFeatures = accessScopeQuery.data?.effectiveFeatures || {}

  const bypassPaidPlans = Boolean(effectiveFeatures?.bypass_plans)
  const canAccessLanguageLab =
    effectiveFeatures?.language_tools !== false && effectiveFeatures?.language_lab !== false
  const canShowLanguageLabTutor = effectiveFeatures?.ai_tutor_lab !== false
  const canShowLanguageLabWriting = effectiveFeatures?.writing_lab !== false
  const canShowVisualizeButton = effectiveFeatures?.visualize_book !== false
  const canShowXrButton = effectiveFeatures?.xr_dashboard !== false
  const canShowAiAvatarButton = effectiveFeatures?.xr_dashboard !== false
  const canShowPaljButton = effectiveFeatures?.adaptive_learning !== false
  // Org-level toggle for the whole Assessments section (ctx key name kept in
  // sync with DESTINATION_CATALOG's 'assessments' visibilityKey).
  const showAssessmentsButton = effectiveFeatures?.assessment_engine !== false
  const isAssessmentAdmin = Boolean(
    (typeof isSuperAdmin === 'function' && isSuperAdmin()) ||
      (typeof isAdmin === 'function' && isAdmin()) ||
      (typeof isCreator === 'function' && isCreator()) ||
      (typeof isTrainer === 'function' && isTrainer())
  )

  const primaryItems = useMemo(
    () =>
      getPrimaryNavItems({
        canAccessLanguageLab,
        canShowLanguageLabTutor,
        canShowLanguageLabWriting,
        canShowVisualizeButton,
        canShowXrButton,
        canShowAiAvatarButton,
        canShowPaljButton,
        showAssessmentsButton,
        isAssessmentAdmin,
      }),
    [
      canAccessLanguageLab,
      canShowLanguageLabTutor,
      canShowLanguageLabWriting,
      canShowVisualizeButton,
      canShowXrButton,
      canShowAiAvatarButton,
      canShowPaljButton,
      showAssessmentsButton,
      isAssessmentAdmin,
    ]
  )

  const secondaryItems = useMemo(
    () =>
      getSecondaryNavItems({
        isSuperAdmin: typeof isSuperAdmin === 'function' && isSuperAdmin(),
        isAdmin: typeof isAdmin === 'function' && isAdmin(),
        isCreator: typeof isCreator === 'function' && isCreator(),
        isTrainer: typeof isTrainer === 'function' && isTrainer(),
        bypassPaidPlans,
      }),
    [isSuperAdmin, isAdmin, isCreator, isTrainer, bypassPaidPlans]
  )
  const manageItems = secondaryItems.filter((item) => item.tone === 'admin')
  const accountItems = secondaryItems.filter((item) => item.tone !== 'admin')

  const isRouteEligible = hasAppSideNav(location.pathname)
  const { data: planSummary, isSuccess: planLoaded } = useQuery({
    queryKey: ['dashboard-plan-summary', user?.id],
    queryFn: () => fetchWithAuth('/api/plans/my-subscription'),
    enabled: !!user?.id && isRouteEligible,
    staleTime: 5 * 60 * 1000,
  })
  const { data: today } = useTodaySummary(isRouteEligible ? user?.id : undefined)
  const tier = normalizePlanTier(planSummary?.usagePlan) || 'free'
  const planLabel = planLoaded ? `${formatPlanTierLabel(tier, tier)} plan` : ''
  // Wait for the plan to load so paid users never see the upgrade card flash.
  const showUpgrade = !bypassPaidPlans && planLoaded && !planSummary?.subscription && tier === 'free'

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false
  )
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(readCollapsedPreference)
  const [tip, setTip] = useState(null)
  // Per-section manual expand/collapse override for primary items that have
  // nested child rows (see PRIMARY_ITEM_CHILDREN in sideNavConfig.js). When a
  // key is absent, the section auto-expands while its page is active.
  const [expandOverrides, setExpandOverrides] = useState({})

  const rail = !isMobile && collapsed

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT
      setIsMobile(mobile)
      if (!mobile) setDrawerOpen(false)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setDrawerOpen(false)
    setTip(null)
  }, [location.pathname])

  const toggleCollapsed = useCallback(() => {
    setTip(null)
    setCollapsed((value) => {
      const next = !value
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0')
      } catch {
        // Preference just won't persist.
      }
      return next
    })
  }, [])

  // The navbar menu button (phones) and `[` (wider screens) both toggle the nav.
  const isMobileRef = useRef(isMobile)
  useEffect(() => {
    isMobileRef.current = isMobile
  }, [isMobile])
  useEffect(() => {
    const onToggle = () => (isMobileRef.current ? setDrawerOpen((open) => !open) : toggleCollapsed())
    const onKey = (event) => {
      if (event.key !== '[' || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target
      if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName || '')) return
      if (!isMobileRef.current) toggleCollapsed()
    }
    window.addEventListener(APP_SIDENAV_TOGGLE_EVENT, onToggle)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener(APP_SIDENAV_TOGGLE_EVENT, onToggle)
      window.removeEventListener('keydown', onKey)
    }
  }, [toggleCollapsed])

  // AppSideNav stays mounted for the whole session (see AppSideNavLayout); pages
  // that don't opt into the sidebar render nothing here. Publish the reserved
  // width so fixed-position pages (XR Dashboard, sticky bars) can offset by it.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const width = isRouteEligible && !isMobile ? (collapsed ? RAIL_WIDTH : EXPANDED_WIDTH) : 0
    document.documentElement.style.setProperty('--app-sidenav-width', `${width}px`)
    return undefined
  }, [isRouteEligible, isMobile, collapsed])

  if (!isRouteEligible) return null

  // Mirror Navbar's `isXrNavbar` dark chrome: XR Dashboard + Virtual World
  // create/edit. AI Avatar intentionally stays on the light theme.
  const isXrTheme =
    location.pathname === '/dashboard/xr' ||
    location.pathname.startsWith('/edit-virtual-world') ||
    location.pathname.startsWith('/create-virtual-world')
  const t = themeClasses(isXrTheme)

  const closeDrawerOnMobile = () => {
    if (isMobile) setDrawerOpen(false)
  }

  const handleAccountAction = (item) => {
    closeDrawerOnMobile()
    if (item.action === 'logout') {
      logout()
        .catch(() => {})
        .finally(() => navigate('/login'))
      return
    }
    if (item.to) navigate(item.to)
  }

  const tipProps = (label) =>
    rail
      ? {
          onMouseEnter: (event) => {
            const rect = event.currentTarget.getBoundingClientRect()
            setTip({ label, top: rect.top + rect.height / 2, left: rect.right + 10 })
          },
          onMouseLeave: () => setTip(null),
          onFocus: (event) => {
            const rect = event.currentTarget.getBoundingClientRect()
            setTip({ label, top: rect.top + rect.height / 2, left: rect.right + 10 })
          },
          onBlur: () => setTip(null),
        }
      : {}

  const rowBase = `relative flex min-w-0 items-center gap-3 rounded-2xl text-sm transition focus:outline-none focus-visible:ring-2 ${t.focus}`

  const renderRow = (item, active) => {
    const Icon = item.icon
    const tint = ITEM_TINT[item.key]?.fg || '#6a1cf6'
    return (
      <NavLink
        to={item.to}
        end={item.exact}
        onClick={closeDrawerOnMobile}
        aria-label={rail ? item.label : undefined}
        {...tipProps(item.label)}
        className={`${rowBase} flex-1 ${rail ? 'h-11 justify-center' : 'px-3 py-2.5'} ${active ? t.rowActive : t.row}`}
      >
        {active && (
          <motion.span
            layoutId="sidenav-active-pill"
            className={`absolute inset-0 rounded-2xl ${t.pill}`}
            transition={{ type: 'spring', stiffness: 520, damping: 40 }}
          >
            <span
              className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full"
              style={{ backgroundColor: isXrTheme ? '#7dd3fc' : tint }}
            />
          </motion.span>
        )}
        <Icon
          size={19}
          stroke={active ? 2 : 1.8}
          className="relative shrink-0 transition-colors"
          style={active ? { color: isXrTheme ? '#7dd3fc' : tint } : undefined}
        />
        {!rail && <span className="relative min-w-0 flex-1 truncate">{item.label}</span>}
        {!rail && item.adminOnly && item.tone !== 'admin' && (
          <span title="Admin / management only" className="relative flex shrink-0 items-center opacity-60">
            <IconUserCog size={14} stroke={1.8} />
          </span>
        )}
      </NavLink>
    )
  }

  const panelWidth = isMobile ? 'min(300px, 86vw)' : rail ? RAIL_WIDTH : EXPANDED_WIDTH

  return (
    <MotionConfig reducedMotion="user">
      {isMobile && drawerOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className={`fixed inset-x-0 top-0 z-[930] backdrop-blur-[2px] ${isXrTheme ? 'bg-slate-950/60' : 'bg-slate-950/35'}`}
          style={{ bottom: 'var(--app-mobile-bottom-nav-height, 0px)' }}
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside
        aria-label="Primary"
        className={`${isMobile ? 'fixed left-0 z-[940]' : 'sticky z-[40]'} transition-[width,min-width] duration-300 ease-out`}
        style={
          isMobile
            ? {
                top: 'var(--app-navbar-height, 75px)',
                height: 'calc(100vh - var(--app-navbar-height, 75px) - var(--app-mobile-bottom-nav-height, 0px))',
                width: 0,
                minWidth: 0,
              }
            : {
                top: 'var(--app-navbar-height, 75px)',
                height: 'calc(100vh - var(--app-navbar-height, 75px))',
                width: panelWidth,
                minWidth: panelWidth,
              }
        }
      >
        <div
          className={`flex h-full flex-col border-r transition-[width,transform] duration-300 ease-out ${t.panel} ${
            isMobile
              ? `rounded-r-[28px] shadow-[0_18px_50px_rgba(15,23,42,0.14)] ${isXrTheme ? 'bg-[#020617]' : 'bg-white'} ${
                  drawerOpen ? 'translate-x-0' : '-translate-x-full'
                }`
              : isXrTheme
                ? 'bg-[rgba(2,6,23,0.55)] backdrop-blur-xl'
                : 'bg-white/35 backdrop-blur-sm'
          }`}
          style={{ width: panelWidth }}
        >
          <div className={`flex items-center px-3 pb-1 pt-3 ${rail ? 'justify-center' : 'justify-between'}`}>
            {!rail && (
              <span className={`pl-2 text-[11px] font-bold uppercase tracking-[0.14em] ${t.muted}`}>Menu</span>
            )}
            {isMobile && (
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close navigation"
                className={`inline-flex h-9 w-9 items-center justify-center rounded-xl transition focus:outline-none focus-visible:ring-2 ${t.iconButton} ${t.focus}`}
              >
                <IconX size={18} stroke={1.9} />
              </button>
            )}
            {!isMobile && (
              <button
                type="button"
                onClick={toggleCollapsed}
                aria-label={rail ? 'Expand navigation' : 'Collapse navigation'}
                aria-expanded={!rail}
                title={rail ? 'Expand ( [ )' : 'Collapse ( [ )'}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-xl transition focus:outline-none focus-visible:ring-2 ${t.iconButton} ${t.focus}`}
              >
                {rail ? <IconLayoutSidebarLeftExpand size={19} stroke={1.8} /> : <IconLayoutSidebarLeftCollapse size={19} stroke={1.8} />}
              </button>
            )}
          </div>

          <LayoutGroup id="app-sidenav">
            <nav className="scrollbar-hide flex-1 overflow-y-auto px-3 pb-3">
              <ul className="space-y-0.5">
                {primaryItems.map((item) => {
                  const active = isPathActive(location.pathname, item)
                  const hasChildren = !rail && item.children?.length > 0
                  const showChildren =
                    hasChildren && (expandOverrides[item.key] !== undefined ? expandOverrides[item.key] : active)
                  return (
                    <li key={item.key}>
                      <div className="flex items-center gap-1">
                        {renderRow(item, active)}
                        {hasChildren && (
                          <button
                            type="button"
                            aria-label={showChildren ? `Collapse ${item.label}` : `Expand ${item.label}`}
                            aria-expanded={showChildren}
                            onClick={() => setExpandOverrides((prev) => ({ ...prev, [item.key]: !showChildren }))}
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition focus:outline-none focus-visible:ring-2 ${t.iconButton} ${t.focus}`}
                          >
                            <IconChevronDown
                              size={15}
                              stroke={2}
                              className={`transition-transform duration-200 ${showChildren ? 'rotate-0' : '-rotate-90'}`}
                            />
                          </button>
                        )}
                      </div>

                      {showChildren && (
                        <motion.ul
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          transition={{ duration: 0.18 }}
                          className={`ml-[1.35rem] mt-0.5 space-y-0.5 overflow-hidden border-l pl-2.5 ${t.divider}`}
                        >
                          {item.children.map((child) => {
                            const ChildIcon = child.icon
                            return (
                              <li key={child.key}>
                                <NavLink
                                  to={child.to}
                                  end={child.exact}
                                  onClick={closeDrawerOnMobile}
                                  className={({ isActive }) =>
                                    `flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-[13px] transition focus:outline-none focus-visible:ring-2 ${t.focus} ${
                                      isActive ? t.childActive : t.row
                                    }`
                                  }
                                >
                                  {ChildIcon && <ChildIcon size={16} stroke={1.8} />}
                                  <span className="min-w-0 truncate">{child.label}</span>
                                  {child.adminOnly && (
                                    <span title="Admin / management only" className="ml-auto flex shrink-0 items-center opacity-60">
                                      <IconUserCog size={13} stroke={1.8} />
                                    </span>
                                  )}
                                </NavLink>
                              </li>
                            )
                          })}
                        </motion.ul>
                      )}
                    </li>
                  )
                })}
              </ul>

              {manageItems.length > 0 && (
                <div className={`mt-4 border-t pt-3 ${t.divider}`}>
                  {!rail && (
                    <p className={`mb-1 flex items-center gap-1.5 px-3 text-[11px] font-bold uppercase tracking-[0.14em] ${t.muted}`}>
                      <IconUserCog size={13} stroke={1.8} />
                      Manage
                    </p>
                  )}
                  <ul className="space-y-0.5">
                    {manageItems.map((item) => (
                      <li key={item.key} className="flex">
                        {renderRow(item, location.pathname === item.to)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </nav>
          </LayoutGroup>

          <div className={`space-y-2 border-t px-3 py-3 ${t.divider}`}>
            {showUpgrade &&
              (rail ? (
                <button
                  type="button"
                  aria-label="Upgrade plan"
                  {...tipProps('Upgrade plan')}
                  onClick={() => navigate('/plans')}
                  className="flex h-11 w-full items-center justify-center rounded-2xl bg-gradient-to-br from-d-primary via-fuchsia-500 to-amber-400 text-white shadow-[0_8px_20px_rgba(106,28,246,0.3)] transition hover:brightness-110"
                >
                  <IconSparkles size={19} stroke={1.9} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    closeDrawerOnMobile()
                    navigate('/plans')
                  }}
                  className="group relative w-full overflow-hidden rounded-2xl bg-gradient-to-br from-d-primary via-[#8b3dff] to-fuchsia-500 p-3 text-left text-white shadow-[0_10px_24px_rgba(106,28,246,0.28)] transition hover:brightness-110"
                >
                  <span className="pointer-events-none absolute -right-6 -top-8 h-20 w-20 rounded-full bg-white/15 blur-xl" aria-hidden="true" />
                  <span className="relative flex items-center gap-2 text-sm font-bold">
                    <IconSparkles size={16} stroke={2} className="transition-transform group-hover:rotate-12" />
                    Unlock more with Learner+
                  </span>
                  <span className="relative mt-0.5 block text-[11px] text-white/80">More AI tutor messages, Visualize and journeys.</span>
                </button>
              ))}

            <ProfileCard
              user={user}
              collapsed={rail}
              dark={isXrTheme}
              planLabel={bypassPaidPlans ? '' : planLabel}
              streak={today?.streak}
              accountItems={accountItems}
              onAction={handleAccountAction}
            />
          </div>
        </div>
      </aside>

      <RailTooltip tip={rail ? tip : null} />
    </MotionConfig>
  )
}
